/* eslint-disable no-unused-expressions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import clientConfig from 'universal-config';
import { isWebview, isMobileDevice } from 'helpers/isMobile';
import { whenZDChatIsLoaded, isCancelChatRoute, isSupportChatRoute } from 'helpers/chat';
import { SUPPORT_CHAT_TAG, CANCEL_CHAT_TAG, CANCEL_CHAT_DEPARTMENT, SUPPORT_CHAT_DEPARTMENT } from 'constants/chat';

export class ZendeskChatSDK extends Component {
  componentDidMount() {
    this.updateZendeskConfiguration(true);
  }

  componentDidUpdate(prevProps) {
    const hasPathUpdated = prevProps.location?.pathname !== this.props.location?.pathname;
    this.updateZendeskConfiguration(hasPathUpdated);
    this.updatePath(hasPathUpdated);
  }

  componentWillUnmount() {
    clearInterval(this.zdTimer);
  }

  suppressChat = shouldSuppressChat => {
    window.zE?.('webWidget', 'updateSettings', {
      webWidget: {
        chat: {
          suppress: shouldSuppressChat,
        },
        contactForm: {
          suppress: true,
        },
      },
    });
  };

  baseChatSetup = () => {
    const { locale, loadUserProfile } = this.props;
    window.zE?.('webWidget:on', 'chat:connected', loadUserProfile);
    window.zE?.('webWidget', 'setLocale', locale);
    this.suppressChat(false);
  };

  supportChatSetup = () => {
    this.removeTag(CANCEL_CHAT_TAG);
    window.zE?.('webWidget', 'updateSettings', {
      webWidget: {
        chat: {
          departments: {
            select: SUPPORT_CHAT_DEPARTMENT,
          },
          tags: [SUPPORT_CHAT_TAG],
        },
      },
    });
  };

  cancelChatSetup = () => {
    this.removeTag(SUPPORT_CHAT_TAG);
    window.zE?.('webWidget:on', 'chat:end', () => {
      this.props.showNpsSurvey();
    });
    window.zE?.('webWidget', 'updateSettings', {
      webWidget: {
        chat: {
          departments: {
            select: CANCEL_CHAT_DEPARTMENT,
          },
          tags: [CANCEL_CHAT_TAG],
        },
      },
    });
  };

  updatePath = hasPathUpdated => {
    if (hasPathUpdated && this.lastLocationCall !== this.props.location.pathname) {
      // ZD's backend doesn't care if we call this multiple times just trying to limit network requests that occur from calling updatePath in two places
      this.lastLocationCall = this.props.location.pathname;
      window.zE?.('webWidget', 'updatePath');
    }
  };

  updateZendeskConfiguration = hasPathUpdated => {
    clearInterval(this.zdTimer);
    if (this.shouldShowChat()) {
      this.zdTimer = whenZDChatIsLoaded(() => {
        // Calling this here to double check we have called update path -- ie going from a page without ZD (so it isn't loaded) to a page with so we want to call after ZD is loaded.
        this.updatePath(hasPathUpdated);
        if (window.zE?.('webWidget:get', 'chat:isChatting')) {
          // This really shouldn't be needed but somehow a couple of times I got into a state where ZD thought I was chatting but also my chat was suppressed
          return this.suppressChat(false);
        }
        this.baseChatSetup();
        if (this.shouldShowSupportChat()) {
          this.supportChatSetup();
        } else {
          this.cancelChatSetup();
        }
      });
    } else {
      this.suppressChat(true);
    }
  };

  shouldShowChat = () =>
    !isWebview(this.props.location.search) &&
    // We don't want anything to load serverside -- weird shit happens if we do. Like the widget double loads and the ZD api breaks in funky ways.
    typeof window !== 'undefined' &&
    (this.shouldShowSupportChat() || this.shouldShowCancelChat() || window.zE?.('webWidget:get', 'chat:isChatting'));

  shouldShowSupportChat = () => {
    const { isSupportChatOn, location } = this.props;
    return isSupportChatOn && isSupportChatRoute(location.pathname) && !isMobileDevice();
  };

  shouldShowCancelChat = () => {
    const { isCancelChatOn, location } = this.props;
    return isCancelChatOn && isCancelChatRoute(location.pathname);
  };

  removeTag(tag) {
    // This is kludgy -- ZD forced upgraded us to v2 but it doesn't have parity with v1 (like the ability to remove tags) so this is how they recommend you access the v1 api. See this for more details: https://support.zendesk.com/hc/en-us/articles/115007912068/comments/360001767648
    window.zE?.(() => {
      window.$zopim(() => {
        window.$zopim.livechat.removeTags(tag);
      });
    });
  }

  render() {
    if ((this.shouldShowChat() || this.hasLoadedScript) && clientConfig().zendesk.key) {
      this.hasLoadedScript = true;
      return (
        <Helmet>
          <script
            id="ze-snippet"
            src={`https://static.zdassets.com/ekr/snippet.js?key=${clientConfig().zendesk.key}`}
            defer
          />
        </Helmet>
      );
    }
    return null;
  }
}

ZendeskChatSDK.propTypes = {
  isSupportChatOn: PropTypes.bool,
  isCancelChatOn: PropTypes.bool,
  location: PropTypes.object,
  locale: PropTypes.string,
  showNpsSurvey: PropTypes.func,
  loadUserProfile: PropTypes.func,
};
